import { FC, HTMLAttributes } from "react";
import { useSelector } from "react-redux";

import { DataDescriptorEntity, Enterprise as App, EnterpriseAppState, FarmEntity } from "@ctra/api";
import { Col, CompassOutlined, Row, Typography, Button, Grid } from "@ctra/components";
import { useTranslation, Enterprise, Grammar } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { Nullable } from "@ctra/utils";
import { Routes } from "@routes";
import { testIds, GAActions } from "@insights";

import styles from "./GamePlanRecommendation.module.less";

const { Title, Paragraph } = Typography;

/**
 * Recommend creating a game plan from the given data descriptor
 * @param dataDescriptorID
 * @param farmID
 * @param className
 * @constructor
 */
export const GamePlanRecommendation: FC<
  HTMLAttributes<HTMLDivElement> & {
    farmID: FarmEntity["id"];
    dataDescriptorID: DataDescriptorEntity["id"];
  }
> = ({ dataDescriptorID, farmID, className }) => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { md } = Grid.useBreakpoint();

  const {
    kpi,
    insights: {
      extensions: { createGamePlan }
    }
  } = Enterprise;

  /**
   * Get the farm
   */
  const farm = useSelector<EnterpriseAppState, Nullable<FarmEntity>>((state) =>
    App.entities.getFarm(state, { farmID })
  );

  const descriptor = useSelector<EnterpriseAppState, DataDescriptorEntity>((state) =>
    App.entities.getDataDescriptor(state, { id: dataDescriptorID })
  );

  const typeName = descriptor?.dataProperties?.typeName;

  /**
   * Translate the KPI name in the right grammatical case
   */
  const kpiName = t<string>(kpi.displayName(typeName), {
    case: Grammar.Case.accusative,
    variant: null,
    makeDefaultValue: true
  });

  /**
   * Create a new game plan of the given farm/data descriptor
   */
  const handleCreateGamePlan = () => {
    const searchParams = new URLSearchParams({
      dataDescriptorID,
      farmID: farmID.toString()
    });

    App.history.push({
      pathname: Routes.app.gamePlans.create.index,
      search: searchParams.toString()
    });
  };

  return (
    <Row
      wrap={false}
      gutter={[16, 0]}
      data-testid={testIds.extensions.createGamePlan.wrapper}
      className={className}
    >
      <Col>
        <CompassOutlined className={styles.Icon} />
      </Col>
      <Col flex={1}>
        <Title level={5}>{t<string>(createGamePlan.title)}</Title>
        <Paragraph>{t<string>(createGamePlan.description)}</Paragraph>
        <Button
          data-testid={testIds.extensions.createGamePlan.button}
          type="primary"
          onClick={() => {
            /* istanbul ignore next */
            trackEvent(GAActions.startGamePlan, { label: `${kpiName} - ${farm?.name}` });
            handleCreateGamePlan();
          }}
          className={styles.Button}
        >
          {t<string>(createGamePlan.action, { kpi: kpiName, variant: md ? "extended" : "short" })}
        </Button>
      </Col>
    </Row>
  );
};
